/*
 Vanilla Scrollyeah
 - Drop-in replacement for the old jQuery scrollyeah plugin
 - No dependencies
 - Auto-inits elements with class `scrollyeah`
 - Public API:
   - Scrollyeah.initAll(root = document)
   - Scrollyeah.forceInit(elementOrSelector, options)
   - instance.refresh()
*/

const DEFAULTS = {
    maxWidth: 999999,
    shadows: true,
    disableIfFit: true,
    centerIfFit: false,
    triggerScrollyeah: false,
};

const instances = new WeakMap();

function readBoolean(val) {
    if (val === true || val === false) return val;
    if (typeof val === 'string') return val === 'true';
    return undefined;
}

function readOptionsFromDataset(el) {
    const o = {};
    if (!el || !el.dataset) return o;
    const read = (name, fallbackAttr) => {
        // prefer dataset camelCase (data-max-width => dataset.maxWidth)
        const ds = el.dataset[name];
        if (typeof ds !== 'undefined') return ds;
        // fallback direct attribute (supports legacy data-maxWidth)
        return el.getAttribute(fallbackAttr);
    };
    const maxWidth = read('maxWidth', 'data-maxWidth') || read('maxWidth', 'data-max-width');
    if (maxWidth) o.maxWidth = Number(maxWidth) || DEFAULTS.maxWidth;
    const shadows = read('shadows', 'data-shadows');
    if (typeof readBoolean(shadows) !== 'undefined') o.shadows = readBoolean(shadows);
    const disableIfFit = read('disableIfFit', 'data-disableIfFit') || read('disableIfFit', 'data-disable-if-fit');
    if (typeof readBoolean(disableIfFit) !== 'undefined') o.disableIfFit = readBoolean(disableIfFit);
    const centerIfFit = read('centerIfFit', 'data-centerIfFit') || read('centerIfFit', 'data-center-if-fit');
    if (typeof readBoolean(centerIfFit) !== 'undefined') o.centerIfFit = readBoolean(centerIfFit);
    const triggerScrollyeah = read('triggerScrollyeah', 'data-triggerScrollyeah') || read('triggerScrollyeah', 'data-trigger-scrollyeah');
    if (typeof readBoolean(triggerScrollyeah) !== 'undefined') o.triggerScrollyeah = readBoolean(triggerScrollyeah);
    return o;
}

function easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
}

class ScrollyeahInstance {
    constructor(root, options = {}) {
        this.root = root;
        this.options = { ...DEFAULTS, ...readOptionsFromDataset(root), ...options };

        this._initialized = false;
        this._dragging = false;
        this._listeners = [];
        this._raf = null;
        this._pos = 0;
        this._minPos = 0;
        this._maxPos = 0;

        this._setup();
        this.refresh();
        this._bind();
        this._initialized = true;
    }

    _setup() {
        if (this.root.dataset.scrollyeahInitialized) return;

        // Wrap inner content: .scrollyeah__wrap > .scrollyeah__shaft
        const wrap = document.createElement('div');
        wrap.className = 'scrollyeah__wrap';
        const shaft = document.createElement('div');
        shaft.className = 'scrollyeah__shaft';

        while (this.root.firstChild) {
            shaft.appendChild(this.root.firstChild);
        }
        wrap.appendChild(shaft);
        this.root.appendChild(wrap);

        this.wrap = wrap;
        this.shaft = shaft;

        // Shadows
        if (this.options.shadows) {
            this.root.classList.add('scrollyeah_shadow');
            this.shadowPrev = document.createElement('i');
            this.shadowPrev.className = 'scrollyeah__shadow scrollyeah__shadow_prev';
            this.shadowNext = document.createElement('i');
            this.shadowNext.className = 'scrollyeah__shadow scrollyeah__shadow_next';
            this.root.appendChild(this.shadowPrev);
            this.root.appendChild(this.shadowNext);
        }

        // Mark initialized
        this.root.dataset.scrollyeahInitialized = 'true';

        // Disable islands
        this.root.querySelectorAll('.scrollyeah__disable').forEach((el) => {
            ['click', 'mousedown', 'mouseup', 'mousemove'].forEach((t) => el.addEventListener(t, (e) => e.stopPropagation()));
            const style = el.style;
            style.userSelect = 'auto';
            style.cursor = 'auto';
        });

        // Parallax children
        this.parallax = Array.from(this.root.querySelectorAll('.scrollyeah__parallax')).map((el) => {
            const rate = Number(el.getAttribute('data-parallaxRate')) || 0;
            return { el, rate };
        });
    }

    _bind() {
        const onResize = () => this.refresh();
        window.addEventListener('resize', onResize);
        window.addEventListener('load', onResize);
        window.addEventListener('orientationchange', onResize);
        this._listeners.push(['resize', onResize], ['load', onResize], ['orientationchange', onResize]);

        // Dragging / Touch
        let startX = 0;
        let startY = 0;
        let downPos = 0;
        let moved = false;
        let lastMoves = [];
        let lastDirection = null;
        let lastUpTime = 0;
        this._suppressClick = false;

        const pointerDown = (clientX, clientY, isTouch, ev) => {
            if (!this._scrollEnabled) return;
            this._dragging = true;
            startX = clientX;
            startY = clientY;
            downPos = this._pos;
            moved = false;
            lastMoves = [[performance.now(), clientX]];
            this.shaft.classList.add('scrollyeah__shaft_grabbing');
            this._suppressClick = false;
            // Не блокируем default на touchstart/mousedown — это ломает tap по ссылкам
        };

        const pointerMove = (clientX, clientY, ev) => {
            if (!this._dragging) return;
            const now = performance.now();
            const dx = clientX - startX;
            const dy = clientY - startY;
            if (!moved) moved = Math.abs(dx) > 10; // горизонтальный сдвиг >10px считаем драгом (мобильный дрейф)
            if (!moved) return;
            ev && ev.preventDefault();

            let newPos = downPos + dx;
            // resistance beyond edges
            if (newPos > this._maxPos) newPos = Math.round(newPos + (this._maxPos - newPos) / 1.5);
            if (newPos < this._minPos) newPos = Math.round(newPos + (this._minPos - newPos) / 1.5);

            this._setPosition(newPos);
            lastMoves.push([now, clientX]);
        };

        const pointerUp = (clientX, _clientY, ev) => {
            if (!this._dragging) return;
            this._dragging = false;
            this.shaft.classList.remove('scrollyeah__shaft_grabbing');
            // Не блокируем default на touchend/mouseup — это ломает переходы по ссылкам

            const upTime = performance.now();

            // Find a reference point around ~300ms ago to compute velocity
            const DRAG_TIMEOUT = 300;
            const ideal = upTime - DRAG_TIMEOUT;
            let ref = lastMoves[0];
            for (let i = 0; i < lastMoves.length; i++) {
                if (Math.abs(lastMoves[i][0] - ideal) <= Math.abs(ref[0] - ideal)) ref = lastMoves[i];
            }
            const timeDiff = Math.max(upTime - ref[0], 1);
            const posDiff = clientX - ref[1];
            const isSwipe = (upTime - ref[0]) <= DRAG_TIMEOUT;

            // inertia in internal coordinates (_pos)
            let target = this._pos;
            let duration = 500;
            if (isSwipe) {
                // momentum proportional to speed, bounded by 25% of visible width
                const speed = posDiff / timeDiff; // px/ms
                const momentum = speed * Math.min(this._containerWidth * 0.25, 300);
                target = Math.round(this._pos + momentum);
            }
            // clamp
            if (target > this._maxPos) {
                target = this._maxPos;
                duration = Math.min(duration, 333);
            } else if (target < this._minPos) {
                target = this._minPos;
                duration = Math.min(duration, 333);
            }

            if (target !== this._pos) {
                this._animateTo(target, duration);
            }

            // Suppress click if drag actually happened to prevent unintended activation of links
            if (moved) {
                this._suppressClick = true;
                setTimeout(() => this._suppressClick = false, 250);
            }
        };

        // Mouse events
        const onMouseDown = (e) => pointerDown(e.pageX, e.pageY, false, e);
        const onMouseMove = (e) => pointerMove(e.pageX, e.pageY, e);
        const onMouseUp = (e) => pointerUp(e.pageX, e.pageY, e);
        this.shaft.addEventListener('mousedown', onMouseDown);
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
        this._listeners.push(['md', onMouseDown], ['mm', onMouseMove], ['mu', onMouseUp]);

        // Touch events
        const onTouchStart = (e) => {
            if (e.touches && e.touches.length === 1) {
                const t = e.touches[0];
                pointerDown(t.pageX, t.pageY, true, e);
            }
        };
        const onTouchMove = (e) => {
            if (e.touches && e.touches.length === 1) {
                const t = e.touches[0];
                pointerMove(t.pageX, t.pageY, e);
            }
        };
        const onTouchEnd = (e) => {
            const t = (e.changedTouches && e.changedTouches[0]) || { pageX: 0, pageY: 0 };
            pointerUp(t.pageX, t.pageY, e);
        };
        this.shaft.addEventListener('touchstart', onTouchStart, { passive: false });
        this.shaft.addEventListener('touchmove', onTouchMove, { passive: false });
        this.shaft.addEventListener('touchend', onTouchEnd, { passive: false });
        this._listeners.push(['ts', onTouchStart], ['tm', onTouchMove], ['te', onTouchEnd]);

        // Wheel (trackpad horizontal scroll)
        const onWheel = (e) => {
            const absX = Math.abs(e.deltaX);
            const absY = Math.abs(e.deltaY);
            if (absX > absY) {
                e.preventDefault();
                // deltaX > 0 — скролл вправо (контент уходит влево) → _pos уменьшается
                let newPos = this._pos - Math.round(e.deltaX);
                newPos = Math.min(this._maxPos, Math.max(this._minPos, newPos));
                this._setPosition(newPos);
            }
        };
        this.shaft.addEventListener('wheel', onWheel, { passive: false });
        this._listeners.push(['wh', onWheel]);

        // Suppress click after drag to avoid accidental link activation on chips
        const onClickCapture = (e) => {
            if (this._suppressClick) {
                e.stopPropagation();
                e.preventDefault();
                this._suppressClick = false;
                moved = false;
            }
        };
        this.shaft.addEventListener('click', onClickCapture, true);
        this._listeners.push(['clk', onClickCapture]);
        // также на корневом контейнере для подстраховки
        this.root.addEventListener('click', onClickCapture, true);
        this._listeners.push(['clk_root', onClickCapture]);
    }

    _setPosition(pos, extra = false) {
        if (this._raf) cancelAnimationFrame(this._raf);
        this._pos = isNaN(pos) ? 0 : pos;
        this.shaft.style.left = `${this._pos}px`;
        this._updateParallax();
        this._updateShadows();
        if (!extra && this.options.triggerScrollyeah) {
            const ev = new CustomEvent('scrollyeah', { detail: this._pos });
            this.root.dispatchEvent(ev);
        }
    }

    _animateTo(target, duration = 333) {
        const start = performance.now();
        const from = this._pos;
        const to = Math.min(this._maxPos, Math.max(this._minPos, target));
        const step = (now) => {
            const t = Math.min(1, (now - start) / duration);
            const k = easeOutCubic(t);
            const val = Math.round(from + (to - from) * k);
            this._setPosition(val);
            if (t < 1) this._raf = requestAnimationFrame(step);
        };
        this._raf = requestAnimationFrame(step);
    }

    _updateParallax() {
        if (!this.parallax || !this.parallax.length) return;
        const center = this._maxPos - (this._maxPos - this._minPos) / 2;
        const getParallaxPos = (pos, rate) => (pos - center) * rate;
        this.parallax.forEach(({ el, rate }) => {
            const p = getParallaxPos(this._pos, rate);
            el.style.left = `${p}px`;
        });
    }

    _updateShadows() {
        if (!this.options.shadows) return;
        if (this._contentWidth > this._containerWidth) {
            this.root.classList.add('scrollyeah_active', 'scrollyeah_shadow');
            if (this._pos <= this._minPos) {
                this.root.classList.remove('scrollyeah_shadow_no-left');
                this.root.classList.add('scrollyeah_shadow_no-right');
            } else if (this._pos >= this._maxPos) {
                this.root.classList.remove('scrollyeah_shadow_no-right');
                this.root.classList.add('scrollyeah_shadow_no-left');
            } else {
                this.root.classList.remove('scrollyeah_shadow_no-left', 'scrollyeah_shadow_no-right');
            }
        } else {
            if (this.options.disableIfFit) {
                this.root.classList.remove('scrollyeah_active');
            } else {
                this.root.classList.add('scrollyeah_active');
            }
            this.root.classList.remove('scrollyeah_shadow', 'scrollyeah_shadow_no-left', 'scrollyeah_shadow_no-right');
        }
    }

    refresh() {
        // width setup
        if (this.wrap) {
            this.wrap.style.width = `${this.options.maxWidth}px`;
        }
        this._contentWidth = this.shaft ? this.shaft.scrollWidth : 0;
        this._containerWidth = this.root.clientWidth;

        this._maxPos = this.options.centerIfFit ? Math.max((this._containerWidth - this._contentWidth) / 2, 0) : 0;
        this._minPos = Math.min(-(this._contentWidth - this._containerWidth), this._maxPos);

        this._scrollEnabled = this._contentWidth > this._containerWidth || !this.options.disableIfFit;
        if (!this._scrollEnabled) {
            this.root.classList.remove('scrollyeah_active');
        } else {
            this.root.classList.add('scrollyeah_active');
        }

        // Clamp current position
        if (this._pos < this._minPos) this._setPosition(this._minPos);
        if (this._pos > this._maxPos) this._setPosition(this._maxPos);

        this._updateParallax();
        this._updateShadows();
    }

    destroy() {
        if (this._raf) cancelAnimationFrame(this._raf);
        this._listeners.forEach(([type, fn]) => {
            switch (type) {
                case 'md': this.shaft.removeEventListener('mousedown', fn); break;
                case 'mm': window.removeEventListener('mousemove', fn); break;
                case 'mu': window.removeEventListener('mouseup', fn); break;
                case 'ts': this.shaft.removeEventListener('touchstart', fn); break;
                case 'tm': this.shaft.removeEventListener('touchmove', fn); break;
                case 'te': this.shaft.removeEventListener('touchend', fn); break;
                case 'wh': this.shaft.removeEventListener('wheel', fn); break;
                default: window.removeEventListener(type, fn); break;
            }
        });
        this._listeners = [];
        this.root.classList.remove('scrollyeah_active', 'scrollyeah_shadow', 'scrollyeah_shadow_no-left', 'scrollyeah_shadow_no-right');
        // unwrap
        if (this.wrap && this.shaft) {
            while (this.shaft.firstChild) {
                this.root.insertBefore(this.shaft.firstChild, this.wrap);
            }
            this.root.removeChild(this.wrap);
        }
        if (this.shadowPrev) this.root.removeChild(this.shadowPrev);
        if (this.shadowNext) this.root.removeChild(this.shadowNext);
        delete this.root.dataset.scrollyeahInitialized;
    }
}

const Scrollyeah = {
    initAll(root = document) {
        const nodes = root.querySelectorAll('.scrollyeah');
        nodes.forEach((el) => {
            if (!instances.get(el)) {
                const inst = new ScrollyeahInstance(el);
                instances.set(el, inst);
            } else {
                instances.get(el).refresh();
            }
        });
    },
    forceInit(elementOrSelector, options = {}) {
        if (!elementOrSelector) return [];
        const els = typeof elementOrSelector === 'string'
            ? document.querySelectorAll(elementOrSelector)
            : (elementOrSelector instanceof Element ? [elementOrSelector] : elementOrSelector);
        const initialized = [];
        els.forEach((el) => {
            const existed = instances.get(el);
            if (existed) {
                existed.refresh();
                initialized.push(existed);
            } else {
                const inst = new ScrollyeahInstance(el, options);
                instances.set(el, inst);
                initialized.push(inst);
            }
        });
        return initialized;
    },
    getInstance(el) {
        return instances.get(el) || null;
    },
};

export default Scrollyeah;


